/**
 * /app/trusts/$id/meetings/new — standalone "Schedule meeting" route.
 *
 * Phase 3 Task 3.5. A direct-linkable entry point for the agenda-builder. The
 * primary surface is the in-route modal on `/app/trusts/$id/meetings` (the
 * "Schedule meeting" CTA); this thin route lets a deep link / notification /
 * external CTA open the same form standalone. Mirrors the thin-delegate pattern
 * of the sibling `resolutions/$rid/sign` route — the route file just wires the
 * URL to a component; the real form lives in `components/screens`.
 *
 * On open it shows the trust meetings screen context with the create modal up;
 * closing the modal (or after a successful create) routes back to the trust's
 * meetings list.
 *
 * Hand-added to `routeTree.gen.ts` mirroring the `meetings` sibling (do NOT
 * regen the routeTree via the plugin — that rewrites custom URLs).
 */

import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router';
import { ScreenShell } from '../../../../components/screens/screen-shell';
import { TrustTabs } from '../../../../components/screens/trust-tabs';
import { MeetingCreateForm } from '../../../../components/screens/meeting-create-form';

export const Route = createFileRoute('/app/trusts/$id/meetings/new')({
  component: MeetingCreateRoute,
});

function MeetingCreateRoute() {
  const { id } = useParams({ from: '/app/trusts/$id/meetings/new' });
  const navigate = useNavigate();

  const backToMeetings = () =>
    void navigate({ to: '/app/trusts/$id/meetings', params: { id } });

  return (
    <ScreenShell
      activeNav="trusts"
      eyebrow="Trustee meetings"
      /* Roman, unpunctuated (cohesion pass): a verb phrase is a heading, not a
         named thing, and a heading never takes a trailing full stop. */
      title="Schedule meeting"
      sub="Set the agenda, attendees, and date for a new trustee meeting."
    >
      <TrustTabs trustId={id} activeTab="meetings" />
      <MeetingCreateForm
        trustId={id}
        open
        onClose={backToMeetings}
        onCreated={backToMeetings}
      />
    </ScreenShell>
  );
}
